import { switchMap, distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import { Component, OnInit } from '@angular/core';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { GraphQlService } from '../../../_services';
import { SessionCalendarAgencyComponent } from './agency/agency.component';
import { SessionService } from '../../../_services/session';

@Component({
    selector: 'session-calendar',
    templateUrl: './calendar.component.html',
    styleUrls: ['./calendar.component.scss'],
    providers: [TranslatePipe],
})
export class SessionCalendarComponent implements OnInit {
    week: any;
    selectedAgencyId: number = SessionCalendarAgencyComponent.ALL_AGENCIES_ID;
    isAllAgenciesTabSelected = true;
    calendarData: any;
    internals: any[];
    externals: any[];
    private static _startDate = '';
    agencies: any[] = [];
    requestsToFetchData = new Subject();

    constructor(
        private sessionService: SessionService,
        private graphQlService: GraphQlService
    ) {}

    ngOnInit(): void {
        // thanks to https://alligator.io/angular/real-time-search-angular-rxjs/
        this.requestsToFetchData
            .pipe(
                distinctUntilChanged(),
                switchMap((startDate) => this.fetchCalendarData(startDate))
            )
            .subscribe((calendarData) => {
                this.calendarData = calendarData.computeCalendarView[0]
                    ? calendarData.computeCalendarView[0].sessions
                    : undefined;
                this.agencies = calendarData.location.map((agency) => ({
                    ...agency,
                    name: agency.name.substr('Zenika '.length).toLowerCase(),
                }));
                this.calendarData = {
                    internals: (this.calendarData.internals || [])
                        .map(this.computeCalendarRow)
                        .map((row) => ({
                            ...row,
                            sessions: row.sessions
                                .map(this.computeSession)
                                .sort((a, b) => a.dayOfWeek - b.dayOfWeek),
                        })),
                    externals: (this.calendarData.externals || [])
                        .map(this.computeCalendarRow)
                        .map((row) => ({
                            ...row,
                            sessions: row.sessions
                                .map(this.computeSession)
                                .sort((a, b) => a.dayOfWeek - b.dayOfWeek),
                        })),
                };
                this.updateDisplayedSession();
            });
        if (SessionCalendarComponent._startDate === '') {
            this.startDateChange(moment().isoWeekday(1).format('YYYY-MM-DD'));
        } else {
            this.startDateChange(SessionCalendarComponent._startDate);
        }
    }

    selectedAgencyIdChange(agencyId) {
        this.selectedAgencyId = agencyId;
        this.isAllAgenciesTabSelected =
            agencyId === SessionCalendarAgencyComponent.ALL_AGENCIES_ID;
        this.updateDisplayedSession();
    }

    startDateChange(event) {
        SessionCalendarComponent._startDate = event;
        this.formatColumnHeaders(event);
        this.calendarData = undefined;
        this.requestsToFetchData.next(event);
    }

    get startDate(): string {
        return SessionCalendarComponent._startDate;
    }

    private formatColumnHeaders(startDate) {
        const startDateMoment = moment(startDate);

        this.week = {
            mon: {
                date: this.formatColumnHeader(startDateMoment, 1),
            },
            tue: {
                date: this.formatColumnHeader(startDateMoment, 2),
            },
            wed: {
                date: this.formatColumnHeader(startDateMoment, 3),
            },
            thu: {
                date: this.formatColumnHeader(startDateMoment, 4),
            },
            fri: {
                date: this.formatColumnHeader(startDateMoment, 5),
            },
        };
    }

    private formatColumnHeader(
        startDateMoment: any,
        isoWeekDay: number
    ): string {
        const prettyFormat = 'dddd D MMM';
        return startDateMoment.isoWeekday(isoWeekDay).format(prettyFormat);
    }

    private fetchCalendarData(startDate) {
        this.sessionService.clearStudentsCountForOneSessionTimeout();
        return this.graphQlService.post({
            query: `
                query($minStartDate: date!, $maxStartDate: date!) {
                    computeCalendarView: compute_calendar_view(
                        args: {
                            start_date_argument: $minStartDate
                            end_date_argument: $maxStartDate
                        }
                    ) {
                        sessions
                    }
                    location(
                        where: {
                            _and: [{ name: { _ilike: "Zenika%" } }, { deleted: { _eq: false } }]
                        }
                    ) {
                        id
                        name
                    }
                }
            `,
            variables: {
                minStartDate: startDate,
                maxStartDate: moment(startDate)
                    .add(5, 'day')
                    .format('YYYY-MM-DD'),
            },
        });
    }

    private computeSession(session) {
        return {
            ...session,
            dayOfWeek: moment(session.startDate).isoWeekday(),
            roundDays: Math.ceil(session.days),
            intra: session.type === 'ONSITE',
        };
    }

    private computeCalendarRow(row) {
        return {
            ...row,
            agencyName: row.agencyName
                ? row.agencyName.substr('Zenika '.length).toLowerCase()
                : undefined,
        };
    }

    private updateDisplayedSession() {
        const selectedAgencySessions: { internals: any[]; externals: any[] } =
            this.selectedAgencyId !==
            SessionCalendarAgencyComponent.ALL_AGENCIES_ID
                ? {
                      internals: this.calendarData.internals.filter(
                          (trainingLocation) =>
                              trainingLocation.agencyId ===
                              this.selectedAgencyId
                      ),
                      externals: this.calendarData.externals.filter(
                          (trainingLocation) =>
                              trainingLocation.agencyId ===
                              this.selectedAgencyId
                      ),
                  }
                : this.calendarData;
        this.internals = selectedAgencySessions.internals;
        this.externals = selectedAgencySessions.externals;
    }

    computeGridItemStyle(session) {
        const { dayOfWeek, roundDays } = session;
        return {
            'grid-column': `${dayOfWeek + 1} /  span ${roundDays}`,
        };
    }
}



// WEBPACK FOOTER //
// ./src/app/_screens/session/calendar/calendar.component.ts